import * as React from "react";
import { useState } from "react";
import { Survey, SurveyTemplateView } from '../../../../../../../amorg-common';
import { createSurveyTemplate, deleteSurveyTemplate } from "../../../../../../../api/surveyClientApi";
import { Button } from "../../../../../../../designSystem/button/Button";
import { ButtonRow } from "../../../../../../../designSystem/button/ButtonRow";
import { Card } from "../../../../../../../designSystem/Card";
import { Flex } from "../../../../../../../designSystem/compositeComponents/FlexHelpers";
import { TextAreaField } from "../../../../../../../designSystem/formField/textArea/TextAreaField";
import { Label } from "../../../../../../../designSystem/Label";
import { MessageCard } from "../../../../../../../designSystem/Message";
import { Modal } from "../../../../../../../designSystem/Modal";
import { H2 } from "../../../../../../../designSystem/typography/Heading";

type SurveyTemplatesModalProps = {
    show:boolean,
    templates:SurveyTemplateView[],
    currentSurvey:Survey,
    disabled?:boolean,
    onSelectTemplate:(templateId:string)=>void,
    onTemplatesChanged:()=>void,
    onClose:()=>void
};
export const SurveyTemplatesModal = (props:SurveyTemplatesModalProps): JSX.Element => 
{
    function handleClose(saved:boolean)
    { 
        if(saved)
            props.onTemplatesChanged();
        props.onClose();
    }
    
    return (
        <>  
            <Modal 
                title={"Anvend skabelon"}
                content={props.show &&
                <>
                    <SaveSurveyTemplatePanel 
                        surveyId={props.currentSurvey.id}
                        onClose={(saved)=>handleClose(saved)} 
                    />

                    <LoadSurveyTemplatePanel 
                        templates={props.templates} 
                        onSelectTemplate={(templateId) => {
                            props.onClose();
                            props.onSelectTemplate(templateId)
                        }}
                        onDeletedTemplate={props.onTemplatesChanged}
                    />
                </>
                }
                showModal={props.show}
                onCancel={props.onClose}/>
        </>
    )
};

type SaveSurveyTemplatePanelProps = {
    surveyId:string,
    onClose:(saved:boolean)=>void
}
const SaveSurveyTemplatePanel = ({ surveyId, onClose }: SaveSurveyTemplatePanelProps): JSX.Element => {
    
    const [didSubmit, setDidSubmit] = useState(false);
    const [description, setDescription] = useState('');

    async function handleSave()
    {
        const result = await createSurveyTemplate(surveyId, description);
        if(result.isSuccess)
            onClose(true);
        else
            setDidSubmit(true);
    }

    async function handleCancel()
    {
        onClose(false);
    }

    return (
        <Card>
            <H2>Gem spørgeskema som skabelon</H2>
            <MessageCard variant="error" hide={!didSubmit} >
                Skal udfyldes med maks. 200 tegn.
            </MessageCard>
            <TextAreaField 
                value={description} 
                onChange={(value) => setDescription(value) } 
                label={"Beskrivelse"} 
                validator={()=> ({valid:true})}
                infoMessage={`${description.length} / 200 tegn`} />

            <ButtonRow marginTop={20}>
                <Button
                    variant="filled"
                    onClick={handleSave}
                    icon={"interface_actions_save"}
                    text={"Gem"} />
                <Button
                    variant="outlined"
                    onClick={handleCancel}
                    icon={"actions_close"}
                    text={"Annullér"} />
            </ButtonRow>
        </Card>


    );
}

type LoadSurveyTemplatePanelProps = { 
    templates: SurveyTemplateView[], 
    onSelectTemplate:(templateId:string)=>void,
    onDeletedTemplate:(templateId:string)=>void 
}
const LoadSurveyTemplatePanel = (props:LoadSurveyTemplatePanelProps): JSX.Element => 
{
    const PAGE_SIZE = 5;
    const [page, setPage] = React.useState(1);

    const pages = Math.ceil(props.templates.length / PAGE_SIZE);
    const startIndex = (page-1)*PAGE_SIZE;
    const templatePage = props.templates.slice(startIndex, startIndex+PAGE_SIZE);

    return (
        <>
            <Card marginTop={20}>
                <H2>Indlæs fra skabelon</H2>
                
            
                <Flex flexDirection="column" gap={16} style={{flex:1}}>
                    {templatePage.map(t => {

                        const deleteHandler = async () => {
                            const result = await deleteSurveyTemplate(t.id);
                            if(result.isSuccess)
                                props.onDeletedTemplate(t.id);
                        };

                        const themeCount = t.themes.length;
                        const questionCount = 
                            t.themes.length === 0
                            ? 0
                            : t.themes
                                .map(t => t.questions.length)
                                .reduce((prev,current)=>{
                                    return prev + current;
                                });
                        return(
                            <Card variant="outlined" key={t.id}>
                                <Flex flexDirection="column" gap={16}>
                                    <Label>
                                        {themeCount===1 ? '1 tema' : `${themeCount} temaer`},&nbsp;
                                        {questionCount} spørgsmål
                                    </Label>
                                    <div>{t.description}</div>
                                    <ButtonRow>
                                        {!t.isGlobalTemplate &&
                                            <Button variant="destructive"
                                                text={"Slet"}
                                                icon="interface_actions_delete"
                                                onClick={deleteHandler} />
                                        }
                                        <Button variant="outlined"
                                            text={"Indlæs"}
                                            icon="interface_actions_download"
                                            onClick={() => props.onSelectTemplate(t.id)} />
                                    </ButtonRow>
                                </Flex>
                            </Card>
                        );        
                    })}
                </Flex>
            </Card>
            
            <div style={{display:'flex', justifyContent:'center', alignItems:'center'}}>
                <Button variant="text"
                    icon="arrows_chevron_chevronBack"
                    onClick={() => page > 1 && setPage(page-1)}
                />
                <span>side {page} af {pages}</span>
                <Button variant="text"
                    icon="arrows_chevron_chevronForward"
                    onClick={() => page < pages && setPage(page+1)}
                />
            </div>
        </>
    );
}